import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    if (this.hasAttribute('data-adapt-media')) {
      this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleMediaSelect, { signal });
      this.#mediaResizeObserver = new ResizeObserver(this.#scheduleMediaHeight);
      this.#mediaResizeObserver.observe(this);
      this.#scheduleMediaHeight();
    }

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#mediaResizeObserver?.disconnect();
    cancelAnimationFrame(this.#mediaHeightFrame);
    this.#activeMediaSlide = undefined;
  }

  /** @type {ResizeObserver | undefined} */
  #mediaResizeObserver;
  #mediaHeightFrame = 0;
  /** @type {HTMLElement | undefined} */
  #activeMediaSlide;

  /** Keep each active image or video at its natural ratio, including after swipes. */
  #scheduleMediaHeight = () => {
    cancelAnimationFrame(this.#mediaHeightFrame);
    this.#mediaHeightFrame = requestAnimationFrame(() => this.#updateMediaHeight());
  };

  /** @param {SlideshowSelectEvent} event */
  #handleMediaSelect = (event) => {
    if (event.target !== this.slideshow) return;
    this.#activeMediaSlide = event.detail.slide;
    this.#scheduleMediaHeight();
  };

  #updateMediaHeight() {
    const slideshow = this.slideshow;
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;
    const width = scroller.clientWidth;
    if (!width) return;
    const slide = this.#activeMediaSlide || slideshow?.slides?.[slideshow.current]
      || slideshow?.querySelector('slideshow-slide');
    const media = slide?.querySelector('.product-media');
    if (!(media instanceof HTMLElement)) return;
    const ratio = Number.parseFloat(media.style.getPropertyValue('--ratio'));
    if (!Number.isFinite(ratio) || ratio <= 0) return;
    const height = `${Math.round(width / ratio * 100) / 100}px`;
    if (scroller.style.height !== height) scroller.style.height = height;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
